import { apiRequest } from '../../../services/api/client';

export interface OfferingsReportQuarter {
  id: number;
  name: string;
  year: number;
  quarter_number: number;
  starts_on: string;
  ends_on: string;
}

export interface OfferingsReportPeriod {
  starts_on: string | null;
  ends_on: string | null;
  dates: string[];
  has_filter: boolean;
}

export interface OfferingsAvailableDate {
  date: string;
  total_amount: number;
  churches_count: number;
  is_missions_offering: boolean;
}

export interface OfferingsSundayBreakdown {
  date: string;
  gross_amount: number;
  missions_amount: number;
  headquarters_amount: number;
  local_amount: number;
  lessons_count: number;
  is_missions_offering: boolean;
}

export interface OfferingsTotals {
  gross_amount: number;
  missions_amount: number;
  headquarters_amount: number;
  local_amount: number;
  lessons_count: number;
}

export interface OfferingsSummaryTotals extends OfferingsTotals {
  churches_count: number;
  classes_count: number;
  sundays_count: number;
  headquarters_rate: number;
}

export interface OfferingsSummary {
  quarter: OfferingsReportQuarter;
  period: OfferingsReportPeriod;
  totals: OfferingsSummaryTotals;
  sundays: OfferingsSundayBreakdown[];
}

export interface OfferingsClassLessonRow {
  date: string;
  lesson_number: number;
  lesson_title: string;
  gross_amount: number;
  missions_amount: number;
  headquarters_amount: number;
  local_amount: number;
  is_missions_offering: boolean;
}

export interface OfferingsClassRow {
  class_id: number;
  class_name: string;
  totals: OfferingsTotals;
  lessons: OfferingsClassLessonRow[];
}

export interface OfferingsChurchRow {
  church_id: number;
  church_name: string;
  level_name: string | null;
  level_order: number | null;
  parent_id: number | null;
  is_headquarters: boolean;
  totals: OfferingsTotals;
  classes: OfferingsClassRow[];
}

export interface OfferingsByChurchReport {
  quarter: OfferingsReportQuarter;
  period: OfferingsReportPeriod;
  churches: OfferingsChurchRow[];
  totals: OfferingsTotals;
}

const buildDatesQuery = (dates: string[]): string => {
  if (dates.length === 0) {
    return '';
  }

  return dates.map((date) => `dates[]=${encodeURIComponent(date)}`).join('&');
};

const buildQuery = (quarterId: string, dates: string[] = []): string => {
  const params: string[] = [`quarter_id=${encodeURIComponent(quarterId)}`];
  const datesQuery = buildDatesQuery(dates);

  if (datesQuery) {
    params.push(datesQuery);
  }

  return `?${params.join('&')}`;
};

export const offeringsReportsService = {
  async availableDates(quarterId: string): Promise<OfferingsAvailableDate[]> {
    const response = await apiRequest<{ data: OfferingsAvailableDate[] }>(
      `/ebd/reports/offerings/available-dates${buildQuery(quarterId)}`
    );

    return response.data;
  },

  async summary(quarterId: string, dates: string[] = []): Promise<OfferingsSummary> {
    const response = await apiRequest<{ data: OfferingsSummary }>(
      `/ebd/reports/offerings/summary${buildQuery(quarterId, dates)}`
    );

    return response.data;
  },

  async byChurch(quarterId: string, dates: string[] = []): Promise<OfferingsByChurchReport> {
    const response = await apiRequest<{ data: OfferingsByChurchReport }>(
      `/ebd/reports/offerings/by-church${buildQuery(quarterId, dates)}`
    );

    return response.data;
  },
};
